import { countryFromText, countryName, isCountryCode } from '#contracts/countries'
import { displayPhone, normalizePhone, phoneProblem } from '#contracts/phone'

/**
 * The registration form's vocabulary: what an edition may ask, and what a valid answer is.
 *
 * **Shared by the API and the console, and the API is still the authority.** The console runs
 * these checks as somebody types so the message under a field is the one the server would send,
 * word for word; the server runs them again on every write, because a check that only happens in
 * the browser is a suggestion.
 *
 * Two kinds of field. **Standard** fields are real columns — badges, meeting lists and invoices
 * read them — so an edition may relabel, hide or require them but not remove or retype them.
 * **Custom** fields are the edition's own, defined as data and answered into a jsonb map.
 */

export const PROFILE_FIELD_TYPES = [
  'text',
  'long_text',
  'select',
  'multi_select',
  'date',
  'number',
  'yes_no',
  'country',
  'phone',
  'email',
  'file',
] as const
export type ProfileFieldType = (typeof PROFILE_FIELD_TYPES)[number]

export function isProfileFieldType(value: unknown): value is ProfileFieldType {
  return (PROFILE_FIELD_TYPES as readonly unknown[]).includes(value)
}

/** Types with a list of choices. The only ones whose `options` mean anything. */
export const CHOICE_TYPES: readonly ProfileFieldType[] = ['select', 'multi_select']

/**
 * Types an edition may mark sensitive. A choice or a yes/no is not a secret worth masking — "Mr"
 * behind dots protects nothing and makes a list unreadable.
 */
export const SENSITIVE_TYPES: readonly ProfileFieldType[] = [
  'text',
  'number',
  'date',
  'phone',
  'email',
  'file',
]

/**
 * The column name in exports and imports, and the key in every stored answer. Fixed once a field
 * exists, because renaming it would orphan every answer already given.
 */
export const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{1,39}$/

export const OPTION_LIMIT = 50
export const LABEL_MAX = 200
export const HELP_MAX = 500
const TEXT_MAX = 500
const LONG_TEXT_MAX = 4000

export type FieldOption = { value: string; label: string }

export type ProfileField = {
  key: string
  label: string
  helpText: string | null
  type: ProfileFieldType
  options: FieldOption[]
  required: boolean
  sensitive: boolean
  /** Empty means every ticket type. */
  ticketTypeIds: string[]
  archived: boolean
}

// --- standard fields ---------------------------------------------------------------------

export const STANDARD_FIELDS = [
  { key: 'phone', label: 'Phone', type: 'phone' },
  { key: 'nationality', label: 'Nationality', type: 'country' },
  { key: 'badgeName', label: 'Badge name', type: 'text' },
  { key: 'jobTitle', label: 'Job title', type: 'text' },
  { key: 'companyName', label: 'Company', type: 'text' },
  { key: 'dietaryRequirements', label: 'Dietary requirements', type: 'long_text' },
] as const satisfies readonly { key: string; label: string; type: ProfileFieldType }[]

export type StandardFieldKey = (typeof STANDARD_FIELDS)[number]['key']

/** The same keys as a tuple, for an enum on the wire. Kept in step by the type below. */
export const STANDARD_FIELD_KEYS = [
  'phone',
  'nationality',
  'badgeName',
  'jobTitle',
  'companyName',
  'dietaryRequirements',
] as const satisfies readonly StandardFieldKey[]

export function isStandardFieldKey(value: unknown): value is StandardFieldKey {
  return STANDARD_FIELDS.some((field) => field.key === value)
}

/** How an edition asks a standard field. `label: null` is the default wording. */
export type StandardSetting = { label: string | null; shown: boolean; required: boolean }
export type StandardSettings = Record<StandardFieldKey, StandardSetting>

/** Shown and optional: what every field was before editions could choose. */
const STANDARD_DEFAULT: StandardSetting = { label: null, shown: true, required: false }

/**
 * The stored jsonb, read defensively. A setting that is missing or malformed is the default, so a
 * field added to `STANDARD_FIELDS` later appears in every edition without a backfill.
 */
export function standardSettings(stored: unknown): StandardSettings {
  const source = typeof stored === 'object' && stored !== null ? stored : {}
  const read = (key: StandardFieldKey): StandardSetting => {
    const raw: unknown = Reflect.get(source, key)
    if (typeof raw !== 'object' || raw === null) return { ...STANDARD_DEFAULT }
    const label: unknown = Reflect.get(raw, 'label')
    const shown: unknown = Reflect.get(raw, 'shown')
    const required: unknown = Reflect.get(raw, 'required')
    return {
      label: typeof label === 'string' && label.trim() !== '' ? label.trim() : null,
      shown: typeof shown === 'boolean' ? shown : true,
      // A hidden field cannot be required: nobody could ever fill it in.
      required: typeof required === 'boolean' && required && shown !== false,
    }
  }
  return {
    phone: read('phone'),
    nationality: read('nationality'),
    badgeName: read('badgeName'),
    jobTitle: read('jobTitle'),
    companyName: read('companyName'),
    dietaryRequirements: read('dietaryRequirements'),
  }
}

export function standardLabel(key: StandardFieldKey, settings: StandardSettings): string {
  return settings[key].label ?? STANDARD_FIELDS.find((field) => field.key === key)?.label ?? key
}

// --- starter fields ----------------------------------------------------------------------

export type StarterField = Omit<ProfileField, 'archived' | 'ticketTypeIds'>

/**
 * The fields every edition starts with — v1's person and registration columns that had nowhere
 * to go in v2.
 *
 * Optional and shown. An edition edits or archives them like any of its own; they are written
 * once and never overwritten (`packages/db/src/profile-fields.ts`).
 */
export const STARTER_FIELDS: readonly StarterField[] = [
  {
    key: 'title',
    label: 'Title',
    helpText: null,
    type: 'select',
    options: ['Mr', 'Mrs', 'Ms', 'Dr', 'Prof'].map((value) => ({ value, label: value })),
    required: false,
    sensitive: false,
  },
  {
    key: 'identity_type',
    label: 'Identity type',
    helpText: null,
    type: 'select',
    options: [
      { value: 'ktp', label: 'KTP' },
      { value: 'passport', label: 'Passport' },
      { value: 'kitas', label: 'KITAS' },
    ],
    required: false,
    sensitive: false,
  },
  {
    key: 'identity_number',
    label: 'Identity number',
    helpText: 'As printed on the document chosen above.',
    type: 'text',
    options: [],
    required: false,
    sensitive: true,
  },
  {
    key: 'city',
    label: 'City',
    helpText: null,
    type: 'text',
    options: [],
    required: false,
    sensitive: false,
  },
  {
    key: 'postal_code',
    label: 'Postal code',
    helpText: null,
    type: 'text',
    options: [],
    required: false,
    sensitive: false,
  },
  {
    key: 'company_phone',
    label: 'Company phone',
    helpText: null,
    type: 'phone',
    options: [],
    required: false,
    sensitive: false,
  },
  // v1 asks these two once for each registration. v2 asks them for each participant.
  {
    key: 'referral_source',
    label: 'Referral source',
    helpText: 'Tell us how you heard about this event.',
    type: 'text',
    options: [],
    required: false,
    sensitive: false,
  },
  {
    key: 'special_requests',
    label: 'Special requests',
    helpText: null,
    type: 'long_text',
    options: [],
    required: false,
    sensitive: false,
  },
]

// --- answers -----------------------------------------------------------------------------

export type AnswerValue = string | number | boolean | string[]
export type Answers = Record<string, AnswerValue>

export type AnswerResult = { ok: true; value: AnswerValue | null } | { ok: false; message: string }

const ok = (value: AnswerValue | null): AnswerResult => ({ ok: true, value })
const fail = (message: string): AnswerResult => ({ ok: false, message })

/** Nothing said. An empty string, an empty list and a null all mean the field was skipped. */
export function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

function isRealDate(text: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false
  const date = new Date(`${text}T00:00:00Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(text)
}

/**
 * One answer, checked and put in its stored shape — or the sentence that says what to fix.
 *
 * A blank answer is `ok` with `null`: whether the field had to be answered is a separate question
 * (`checkDetails`), asked at submission rather than on every keystroke of a draft.
 *
 * `tenantPrefix` is where this edition's uploads live. A file answer is a storage key, and on a
 * presigned download the key is the authorization decision, so a key from anywhere else is
 * refused rather than stored.
 */
export function checkAnswer(
  field: Pick<ProfileField, 'type' | 'options'>,
  value: unknown,
  tenantPrefix?: string,
): AnswerResult {
  if (isBlank(value)) return ok(null)

  switch (field.type) {
    case 'text':
    case 'long_text': {
      if (typeof value !== 'string') return fail('Enter text.')
      const max = field.type === 'text' ? TEXT_MAX : LONG_TEXT_MAX
      const text = value.trim()
      return text.length > max ? fail(`Keep it under ${max} characters.`) : ok(text)
    }
    case 'email': {
      if (typeof value !== 'string') return fail('Enter an email address.')
      const text = value.trim().toLowerCase()
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text) && text.length <= 320
        ? ok(text)
        : fail('Enter an email address, like name@example.com.')
    }
    case 'select': {
      if (typeof value !== 'string') return fail('Choose one of the options.')
      return field.options.some((option) => option.value === value)
        ? ok(value)
        : fail('Choose one of the options.')
    }
    case 'multi_select': {
      if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
        return fail('Choose from the options.')
      }
      const chosen = [...new Set(value.filter((item): item is string => typeof item === 'string'))]
      const allowed = new Set(field.options.map((option) => option.value))
      if (chosen.some((item) => !allowed.has(item))) return fail('Choose from the options.')
      // Stored in the form's order, so two people who ticked the same boxes store the same list.
      return ok(field.options.map((option) => option.value).filter((item) => chosen.includes(item)))
    }
    case 'date':
      return typeof value === 'string' && isRealDate(value)
        ? ok(value)
        : fail('Enter a date as year, month and day.')
    case 'number':
      return typeof value === 'number' && Number.isFinite(value)
        ? ok(value)
        : fail('Enter a number.')
    case 'yes_no':
      return typeof value === 'boolean' ? ok(value) : fail('Choose yes or no.')
    case 'country':
      return isCountryCode(value) ? ok(value) : fail('Choose a country from the list.')
    case 'phone': {
      if (typeof value !== 'string') return fail('Enter a phone number.')
      const problem = phoneProblem(value)
      return problem === undefined ? ok(value) : fail(problem)
    }
    case 'file':
      if (typeof value !== 'string' || value.length > 500) return fail('Upload the file again.')
      if (tenantPrefix !== undefined && !value.startsWith(tenantPrefix)) {
        return fail('That file does not belong to this event. Upload it again.')
      }
      return ok(value)
  }
  return fail('That answer is not one this field accepts.')
}

/**
 * A spreadsheet cell, read into an answer.
 *
 * More forgiving than `checkAnswer`, because a person typed it into Excel rather than choosing it
 * from a control: a choice matches its label as well as its value, `Yes`/`No`/`1`/`0` are yes or
 * no, several choices are separated by `;`, a phone is read as national in the edition's country
 * unless it carries a `+`, and a country may be its English name.
 */
export function readCell(
  field: Pick<ProfileField, 'type' | 'options'>,
  cell: string,
  defaultCountry: string,
): AnswerResult {
  const text = cell.trim()
  if (text === '') return ok(null)

  const option = (item: string) => {
    const lower = item.trim().toLowerCase()
    return field.options.find(
      (choice) => choice.value.toLowerCase() === lower || choice.label.toLowerCase() === lower,
    )?.value
  }

  switch (field.type) {
    case 'select': {
      const value = option(text)
      return value === undefined
        ? fail(`must be one of ${field.options.map((choice) => choice.label).join(', ')}`)
        : ok(value)
    }
    case 'multi_select': {
      const items = text.split(';').filter((item) => item.trim() !== '')
      const values = items.map(option)
      if (values.includes(undefined)) {
        return fail(`each choice must be one of ${field.options.map((c) => c.label).join(', ')}`)
      }
      return checkAnswer(
        field,
        values.filter((value): value is string => value !== undefined),
      )
    }
    case 'yes_no': {
      const lower = text.toLowerCase()
      if (['yes', 'y', 'true', '1', 'ya'].includes(lower)) return ok(true)
      if (['no', 'n', 'false', '0', 'tidak'].includes(lower)) return ok(false)
      return fail('must be Yes or No')
    }
    case 'number': {
      const number = Number(text.replaceAll(',', ''))
      return Number.isFinite(number) ? ok(number) : fail('must be a number')
    }
    case 'country': {
      const code = countryFromText(text)
      return code === undefined ? fail('must be a country code or name') : ok(code)
    }
    case 'phone': {
      const result = normalizePhone(text, defaultCountry)
      return result.ok ? ok(result.value) : fail(result.message)
    }
    case 'file':
      return fail('files cannot be imported from a spreadsheet')
    default:
      return checkAnswer(field, text)
  }
}

/**
 * An answer as words, for reading: a choice by its label, a country by its name, a phone spaced,
 * a date in the reader's format, a file by its name. The console and the portal both show answers
 * this way, so a delegate and the desk read the same thing.
 */
export function describeAnswer(
  field: Pick<ProfileField, 'type' | 'options'>,
  value: unknown,
  locale?: string,
): string {
  const label = (item: string) =>
    field.options.find((option) => option.value === item)?.label ?? item
  switch (field.type) {
    case 'select':
      return typeof value === 'string' ? label(value) : ''
    case 'multi_select':
      return Array.isArray(value) ? value.map((item) => label(String(item))).join(', ') : ''
    case 'yes_no':
      return value === true ? 'Yes' : value === false ? 'No' : ''
    case 'country':
      return typeof value === 'string' ? countryName(value, locale) : ''
    case 'phone':
      return typeof value === 'string' ? displayPhone(value) : ''
    case 'date': {
      if (typeof value !== 'string') return ''
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
      const date = new Date(`${value}T00:00:00Z`)
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(date)
    }
    case 'file':
      return typeof value === 'string' ? (value.split('/').at(-1) ?? value) : ''
    default:
      return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
  }
}

/** `•••• 4567` — enough to recognize a number you hold, not enough to use one you do not. */
export function maskAnswer(value: AnswerValue): string {
  const text = Array.isArray(value) ? value.join(', ') : String(value)
  return text.length <= 4 ? '••••' : `•••• ${text.slice(-4)}`
}

/** `Identity number` → `identity_number`. The suggested column name; editable while creating. */
export function keyFromLabel(label: string): string {
  const slug = label
    .normalize('NFKD')
    .replaceAll(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '_')
    .replaceAll(/^_+|_+$/g, '')
    .slice(0, 40)
    .replace(/_+$/, '')
  const keyed = /^[a-z]/.test(slug) ? slug : `field_${slug}`.slice(0, 40)
  return keyed.length >= 2 ? keyed : 'field'
}

export function appliesTo(field: Pick<ProfileField, 'ticketTypeIds'>, ticketTypeId: string) {
  return field.ticketTypeIds.length === 0 || field.ticketTypeIds.includes(ticketTypeId)
}

// --- a participant's details, all at once -------------------------------------------------

export type RegistrationForm = {
  fields: ProfileField[]
  standard: StandardSettings
  defaultCountry: string
}

export type StandardValues = Partial<Record<StandardFieldKey, string | null | undefined>>

export type DetailIssue = {
  /** A standard key, or `answers.<key>` for a custom field. What the console focuses. */
  field: string
  label: string
  message: string
}

export type DetailsResult = {
  issues: DetailIssue[]
  /** Only the answers this ticket type asks, in their stored shape. Blanks are absent. */
  answers: Answers
  /** Phone and nationality in their stored shape; everything else trimmed. */
  standard: StandardValues
}

/**
 * Everything wrong with one participant's details, and the details made storable.
 *
 * **Every issue, never the first** — the same rule as `checkParticipantRequirements`: a
 * delegation of forty fixed one message per round trip is a form nobody finishes.
 *
 * `requireComplete` adds the required checks. Off while a draft is being typed, on at submission.
 * `skipRequiredFiles` is for imports: a spreadsheet cannot carry a file, so a required upload is
 * something the delegate adds later rather than a reason to refuse the row.
 *
 * Answers to fields this ticket type does not ask are dropped, not refused: they are what is left
 * behind when somebody changes a participant's ticket, and the form has already said so.
 * Answers to fields that do not exist, or have been archived, are refused — they are a stale form
 * or a typo in an import, and dropping them would lose what somebody meant to say.
 */
export function checkDetails(
  form: RegistrationForm,
  details: { ticketTypeId: string; standard: StandardValues; answers: Record<string, unknown> },
  options: { requireComplete: boolean; skipRequiredFiles?: boolean; tenantPrefix?: string } = {
    requireComplete: false,
  },
): DetailsResult {
  const issues: DetailIssue[] = []
  const standard: StandardValues = {}

  for (const definition of STANDARD_FIELDS) {
    const setting = form.standard[definition.key]
    const label = standardLabel(definition.key, form.standard)
    const raw = details.standard[definition.key]

    if (isBlank(raw)) {
      standard[definition.key] = null
      if (options.requireComplete && setting.shown && setting.required) {
        issues.push({ field: definition.key, label, message: `${label} is required.` })
      }
      continue
    }

    if (definition.type === 'phone') {
      const result = normalizePhone(String(raw), form.defaultCountry)
      if (result.ok) standard.phone = result.value
      else issues.push({ field: 'phone', label, message: result.message })
    } else if (definition.type === 'country') {
      if (isCountryCode(raw)) standard.nationality = raw
      else issues.push({ field: 'nationality', label, message: 'Choose a country from the list.' })
    } else {
      standard[definition.key] = String(raw).trim()
    }
  }

  const byKey = new Map(form.fields.map((field) => [field.key, field]))
  const answers: Answers = {}

  for (const [key, value] of Object.entries(details.answers)) {
    const field = byKey.get(key)
    if (field === undefined || field.archived) {
      if (isBlank(value)) continue
      issues.push({
        field: `answers.${key}`,
        label: field?.label ?? key,
        message:
          field === undefined
            ? 'This question is not on the form.'
            : 'This question is no longer asked. Remove the answer.',
      })
      continue
    }
    if (!appliesTo(field, details.ticketTypeId)) continue

    const result = checkAnswer(field, value, options.tenantPrefix)
    if (!result.ok) {
      issues.push({ field: `answers.${key}`, label: field.label, message: result.message })
    } else if (result.value !== null) {
      answers[key] = result.value
    }
  }

  if (options.requireComplete) {
    for (const field of form.fields) {
      if (field.archived || !field.required || !appliesTo(field, details.ticketTypeId)) continue
      if (options.skipRequiredFiles === true && field.type === 'file') continue
      const already = issues.some((issue) => issue.field === `answers.${field.key}`)
      if (!already && answers[field.key] === undefined) {
        issues.push({
          field: `answers.${field.key}`,
          label: field.label,
          message: `${field.label} is required.`,
        })
      }
    }
  }

  return { issues, answers, standard }
}
